import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    type OnInit,
    signal,
    type WritableSignal,
} from '@angular/core';
import {FormsModule, NgForm} from '@angular/forms';
import {ErrorService} from '@dv/kitadmin/core/errors';
import {Rechnung, RechnungsStatus} from '@dv/kitadmin/models';
import {CompletableDialogBase, type CompletableDialogModel} from '@dv/kitadmin/ui';
import {
    AuthStore,
    ButtonListComponent,
    DatepickerTextfieldComponent,
    LoadingButtonComponent,
} from '@dv/shared/angular';
import {PERMISSION} from '@dv/shared/authentication/model';
import type {CustomRechnungsPositionTemplate} from '@dv/shared/backend/model/custom-rechnungs-position-template';
import {GebuehrType} from '@dv/shared/backend/model/gebuehr-type';
import {JaxRechnungsRevisionUpdate} from '@dv/shared/backend/model/jax-rechnungs-revision-update';
import {
    checkPresent,
    DvbDateUtil,
    DvbRestUtil,
    DvbUtil,
    isNullish,
    nextId,
    Nullish,
    Persisted,
    RechnungsPositionType,
} from '@dv/shared/code';
import {TranslocoModule} from '@jsverse/transloco';
import moment from 'moment/moment';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {determineAvailableStateTransitions} from '../../../service/fakturaUtil';

export interface ModifyRechnungDialogModel
    extends CompletableDialogModel<JaxRechnungsRevisionUpdate> {
    rechnung: Persisted<Rechnung>;
    state?: Readonly<RechnungsStatus>;
}

interface CustomPostionFormModel {
    uiId: string;
    text: string | Nullish;
    betrag: number | Nullish;
    disabled: boolean;
}

@Component({
    selector: 'dv-modify-rechnung',
    imports: [
        FormsModule,
        TranslocoModule,
        TooltipModule,
        LoadingButtonComponent,
        ButtonListComponent,
        DatepickerTextfieldComponent,
    ],
    templateUrl: './modify-rechnung.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModifyRechnungComponent extends CompletableDialogBase<JaxRechnungsRevisionUpdate>
    implements ModifyRechnungDialogModel, OnInit {

    private readonly errorService = inject(ErrorService);
    private readonly authStore = inject(AuthStore);

    @Input({required: true}) public rechnung!: Persisted<Rechnung>;
    @Input() public state?: Readonly<RechnungsStatus>;

    public externalStatus: WritableSignal<RechnungsStatus | null> = signal(null);

    // Rechnungsgebühren
    public customPositions: CustomPostionFormModel[] = [];
    public editable: boolean = false;
    public readonly depotType = GebuehrType.DEPOT;

    // Frist anpassung
    public zahlungsFrist: moment.Moment | null = null;

    // Status
    public availableStateTransitions: Readonly<RechnungsStatus>[] = [];

    public ngOnInit(): void {
        this.editable = this.mayEditRechnung();
        this.customPositions = this.rechnung.customPositionen
            .filter(pos => pos.positionType === RechnungsPositionType.BETRAG)
            .map(({text, betrag, gebuehrType}): CustomPostionFormModel => ({
                uiId: nextId(),
                text,
                betrag,
                disabled: gebuehrType === GebuehrType.DEPOT,
            }));

        this.zahlungsFrist = moment(this.rechnung.zahlungsFrist);
        this.externalStatus.set(this.state ?? this.rechnung.externalStatus);

        this.availableStateTransitions = determineAvailableStateTransitions(this.rechnung);
    }

    public submitForm(form: NgForm): void {
        const validFrist = DvbDateUtil.isValidMoment(this.zahlungsFrist);
        this.errorService.handleValidationError(validFrist, 'ERRORS.ERR_INVALID_DATE');
        this.errorService.handleValidationError(!form.invalid, 'ERRORS.ERR_INCOMPLETE_FORM');

        const externalStatus = this.externalStatus();
        if (form.invalid! || !validFrist || isNullish(externalStatus)) {
            return;
        }

        const customRechnungsPositionen = this.customPositions
            .map((p): CustomRechnungsPositionTemplate => ({
                text: checkPresent(p.text),
                betrag: checkPresent(p.betrag),
                anzahl: 1,
                positionType: 'BETRAG',
            }));

        this.submit({
            zahlungsFrist: DvbRestUtil.momentToLocalDateChecked(this.zahlungsFrist),
            status: checkPresent(externalStatus).toRestObject().status,
            customRechnungsPositionen,
        });
    }

    public addGebuehr(): void {
        this.customPositions.push({uiId: nextId(), text: undefined, betrag: undefined, disabled: false});
    }

    public removeGebuehr(position: CustomPostionFormModel): void {
        DvbUtil.removeFromArray(position, this.customPositions);
    }

    private mayEditRechnung(): boolean {
        if (!this.rechnung.beforeRechnungsLaufLock) {
            return true;
        }

        const permission = `${PERMISSION.GRANULAR_KINDER_ORT.FAKTURA_RECHNUNGSLAUF_LOCK_OVERRIDE}${this.rechnung.kitaId}`;

        return this.authStore.hasPermission(permission);
    }
}
